import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  AppDatabaseUsage,
  ContainerAppFeature,
  ContainerBillingChangePreview,
  ContainerFeature,
  Plan,
  User,
} from '@back4app2/sdk';
import back4app2 from '../back4app2';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorMessage from '../components/ErrorMessage';
import { DsButton, Eyebrow, StatusDot, StatusPill } from '../components/ds';
import { ChevronRightIcon } from '../components/ds/icons';
import useContainerPurchase from '../hooks/useContainerPurchase';
import { planCheckoutSpec, planDisplayName } from '../utils/planNames';
import { StorageUsageStrip } from './database/StorageUsage';

/**
 * Settings > Plan: the plan list and the extras on the left, and one rail on
 * the right that is always about money.
 *
 * The rail is a small state machine. At rest on a paid app it shows the CURRENT
 * plan, its total and the way out (cancel). As soon as a row or a pack count
 * differs from what the app holds it turns into the SUMMARY of the change,
 * with the one button that makes it real. From there: CANCEL confirms leaving
 * the plan, PAY shows what the card is charged now (prorated, quoted by the
 * server) before it is, and DONE says the money landed and where the deploy it
 * started can be watched. Everything the old bottom bar and three modals did,
 * in the one place the price was already being read.
 *
 * How the app is billed decides what paying does:
 *
 *   SUBSCRIPTION  the app has its own subscription. The summary leads to the
 *                 PAY step — the prorated amount is quoted first — and
 *                 confirming replaces the subscription's lines in one update.
 *   CHECKOUT      a Free app, or a paid one whose legacy subscription could not
 *                 be adopted (`replacesPreviousPlan`). The summary opens the
 *                 Paddle checkout with the plan and every pack as lines; for
 *                 the latter it is the full price, and the old subscription is
 *                 cancelled by the team.
 *
 * Two rules come from the server and are mirrored only so the controls do not
 * offer what will be refused: a pack can be ADDED only where the extra is sold
 * on the chosen plan, and the last database pack cannot be removed yet.
 */

type BillingMode = 'SUBSCRIPTION' | 'CHECKOUT';
type Step = 'select' | 'cancel' | 'pay' | 'done';

const formatRAM = (ram?: number) =>
  typeof ram === 'number' ? (ram >= 1024 ? `${ram / 1024} GB` : `${ram} MB`) : '';
const formatTransfer = (gb?: number) =>
  typeof gb === 'number' ? (gb >= 1000 ? `${gb / 1000} TB` : `${gb} GB`) : '';
const money = (amount: number) => `$${amount.toFixed(2)}`.replace(/\.00$/, '');
const longDate = (date: Date) =>
  date.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
const shortDate = (date: Date) =>
  date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

/** "1 vCPU · 2 GB" — the row's first line, and the spec beside a name. */
const planSpec = (plan?: Plan) =>
  plan ? [`${plan.maxCPU} vCPU`, formatRAM(plan.maxRAM)].filter(Boolean).join(' · ') : '';

/** What the plan family actually means, in the words that matter. */
const cpuKind = (plan: Plan) =>
  plan.price === 0 ? 'free' : plan.name.toLowerCase().includes('dedicated') ? 'dedicated' : 'shared';

const errorText = (e: unknown, fallback: string) =>
  e instanceof Error && e.message ? e.message : fallback;

/**
 * One section of the box. Stacked on a phone, side by side from `lg`, with a
 * hairline between neighbours either way.
 */
const SECTION =
  'flex flex-col gap-1.5 px-5 py-[18px] border-navy-500 border-b last:border-b-0 lg:border-b-0 lg:border-r lg:last:border-r-0';

/** The first section: eyebrow, a title line, a line of spec. */
const RailHead = ({
  eyebrow,
  eyebrowClassName = '',
  aside,
  children,
}: {
  eyebrow: string;
  eyebrowClassName?: string;
  aside?: React.ReactNode;
  children: React.ReactNode;
}) => (
  <div className={`${SECTION} flex-1 min-w-0 justify-center`}>
    <div className="flex items-center gap-2">
      <Eyebrow className={eyebrowClassName}>{eyebrow}</Eyebrow>
      {aside ? <span className="ml-auto">{aside}</span> : null}
    </div>
    {children}
  </div>
);

/** A priced line of the change: what, its spec, and what it costs. */
const RailLine = ({ label, spec, price }: { label: string; spec?: string; price?: number }) => (
  <div className="flex items-baseline gap-3">
    <div className="flex-1 min-w-0 flex flex-col gap-0.5">
      <span className="text-ds-135 text-navy-100">{label}</span>
      {spec ? <span className="font-courrier text-ds-115 text-navy-300">{spec}</span> : null}
    </div>
    {typeof price === 'number' ? (
      <span className="font-courrier text-ds-135 text-white">{money(price)}</span>
    ) : null}
  </div>
);

/** The big number: an eyebrow, the amount, and its unit. */
const RailTotal = ({ eyebrow, amount, unit = '/mo' }: { eyebrow: string; amount: string; unit?: string | null }) => (
  <div className="flex items-baseline gap-1.5">
    <Eyebrow>{eyebrow}</Eyebrow>
    <span className="ml-auto font-sora text-[28px] font-semibold tracking-[-0.02em] leading-none text-white">{amount}</span>
    {unit ? <span className="font-courrier text-ds-115 text-navy-300">{unit}</span> : null}
  </div>
);

const PlanCheckout = ({
  appId,
  isOwner = true,
  plan: currentPlan,
  planCancelsAt,
  pendingPlan,
  planChangesAt,
  hasPaddleSubscription,
  paymentPastDueAt,
  isPendingPayment,
  user,
  plans,
  isLoadingPlans,
  loadingPlansErrorMessage,
  onPlanCanceled,
}: {
  appId?: string;
  isOwner?: boolean;
  plan?: Plan;
  planCancelsAt?: Date;
  pendingPlan?: Plan;
  planChangesAt?: Date;
  hasPaddleSubscription?: boolean;
  paymentPastDueAt?: Date;
  isPendingPayment?: boolean;
  user?: User;
  plans?: Plan[];
  isLoadingPlans: boolean;
  loadingPlansErrorMessage?: string;
  /** The plan was cancelled, kept or changed on the server: reload the app. */
  onPlanCanceled?: () => void;
}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Lazy: Paddle loads when the checkout opens, not when the tab renders.
  // Plain overlay: no glass on this page.
  const { purchase, checkoutModal } = useContainerPurchase({ lazy: true, plainOverlay: true });

  // Fetched here rather than lifted into the screen's reducer: the extras are
  // only ever read by this section, and the screen already carries enough.
  const [owned, setOwned] = useState<ContainerAppFeature[] | undefined>();
  const [catalog, setCatalog] = useState<ContainerFeature[] | undefined>();
  // How much of the database extra is in use. Its own request, and a cheap
  // one: it reads our own rows only, so it never opens a connection to the
  // app's database and answers the same for an app that is stopped.
  const [usage, setUsage] = useState<AppDatabaseUsage | undefined>();
  const [cycleEnd, setCycleEnd] = useState<Date | undefined>();
  const [step, setStep] = useState<Step>('select');
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [preview, setPreview] = useState<ContainerBillingChangePreview>();
  const [isWorking, setIsWorking] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  /**
   * The money went through. `charged` separates a checkout and an upgrade,
   * which take money now, from a downgrade that comes out as a credit: calling
   * that a payment would be a lie on the one screen where money is the subject.
   */
  const [paid, setPaid] = useState<{ charged: boolean; amount?: string; plan?: Plan } | null>(null);

  useEffect(() => {
    if (!appId) {
      return;
    }

    let canceled = false;

    back4app2.containerAppFeatures(appId)
      .then((rows) => { if (!canceled) setOwned(rows); })
      .catch((e) => {
        // The plan list is the subject here; a failed extras read must not
        // take the section down with it.
        console.error('could not load the app extras', e);
        if (!canceled) setOwned([]);
      });
    back4app2.containerFeatures()
      .then((rows) => { if (!canceled) setCatalog(rows); })
      .catch((e) => {
        console.error('could not load the extras catalog', e);
        if (!canceled) setCatalog([]);
      });
    back4app2.appDatabaseUsage(appId, 30)
      .then((result) => { if (!canceled) setUsage(result); })
      .catch((e) => console.error('could not load the database usage', e));

    return () => { canceled = true; };
  }, [appId]);

  const isPaid = Boolean(currentPlan && currentPlan.price > 0);

  // The renewal date is the one line of the rail at rest that is not already
  // on the app. One call, and only for an app with a subscription to read.
  useEffect(() => {
    if (!appId || !isPaid || !hasPaddleSubscription || !isOwner) {
      return;
    }

    let canceled = false;

    back4app2.containerBillingCycleEnd(appId)
      .then((end) => { if (!canceled && end) setCycleEnd(new Date(end)); })
      // The rail works without the date; it just says less.
      .catch((e) => console.error('could not read the billing cycle', e));

    return () => { canceled = true; };
  }, [appId, isPaid, hasPaddleSubscription, isOwner]);

  // A cancelled plan is not a former plan: it runs, and it was paid for, until
  // the date. Everything below reads this instead of hiding the plan away.
  const cancelsOn = planCancelsAt ? new Date(planCancelsAt) : undefined;
  const isScheduledToCancel = Boolean(cancelsOn && cancelsOn.getTime() > Date.now());
  // The same shape, because it is the same promise: the app keeps what it paid
  // for, and the smaller plan starts when the period does. Until then
  // `currentPlan` is still the bigger one — it is what is running.
  const changesOn = planChangesAt ? new Date(planChangesAt) : undefined;
  const isScheduledToChange = Boolean(pendingPlan && changesOn && changesOn.getTime() > Date.now());

  /**
   * Which way this app changes plan.
   *
   * A paid app with no subscription of its own was paid through the legacy
   * checkout. The server looks for that subscription when the screen opens and
   * adopts it when it safely can (SUBSCRIPTION: changed in place). When it
   * cannot, the change is a new checkout at the full price and the team cancels
   * the old one (CHECKOUT). Neither answer blocks paying, and a failed lookup
   * is a checkout. Until it answers, the list only shows the current plan.
   */
  const needsBillingPath = isPaid && !hasPaddleSubscription;
  const [billingPath, setBillingPath] = useState<BillingMode | undefined>();

  useEffect(() => {
    if (!appId || !needsBillingPath || !isOwner) {
      return;
    }

    let canceled = false;

    back4app2.resolveContainerBillingPath(appId)
      .then((path) => { if (!canceled) setBillingPath(path); })
      .catch((e) => {
        console.error('could not resolve how this app is billed', e);
        if (!canceled) setBillingPath('CHECKOUT');
      });

    return () => { canceled = true; };
  }, [appId, needsBillingPath, isOwner]);

  const isLegacyBilling = needsBillingPath && !billingPath;
  const replacesPreviousPlan = needsBillingPath && billingPath === 'CHECKOUT';
  const mode: BillingMode = !isPaid || replacesPreviousPlan ? 'CHECKOUT' : 'SUBSCRIPTION';

  // The picked plan: the current one until another is picked. A Free app has
  // nothing picked, and its rail asks for a plan.
  const [draftPlanId, setDraftPlanId] = useState<string | undefined>(isPaid ? currentPlan?.id : undefined);
  const draftPlan = plans?.find((plan) => plan.id === draftPlanId);
  const freePlan = plans?.find((plan) => plan.price === 0);

  /**
   * `?add=<kind>` opens this screen with an extra already chosen — the Database
   * section's offer sends `database`, so the reader lands on the plan that
   * sells it with the extra in the basket and only has to pay. The KIND, not
   * an id: what they meant is "I want a database", and the id is looked up
   * here, where the catalogue already is.
   */
  const wantedKind = searchParams.get('add') || undefined;
  const wantedFeature = wantedKind ? catalog?.find((row) => row.kind === wantedKind) : undefined;
  /** Seeded once — after that the list is the reader's. */
  const hasSeeded = useRef(false);

  const ownedCounts = useMemo(() => {
    const result: Record<string, number> = {};

    for (const row of owned || []) {
      if (row.status === 'ACTIVE') {
        result[row.featureId] = (result[row.featureId] || 0) + row.quantity;
      }
    }

    return result;
  }, [owned]);

  useEffect(() => {
    if (!wantedFeature || !plans || hasSeeded.current) {
      setCounts(ownedCounts);
      return;
    }

    // The cheapest paid plan this extra is actually sold on. An app already
    // paying keeps its plan: it came for the extra, not for a downgrade.
    const entry = plans
      .filter((candidate) => candidate.price > 0 && wantedFeature.planIds.includes(candidate.id))
      .sort((a, b) => a.price - b.price)[0];

    if (!entry) {
      setCounts(ownedCounts);
      return;
    }

    hasSeeded.current = true;
    setDraftPlanId((current) => current || entry.id);
    setCounts({ ...ownedCounts, [wantedFeature.id]: Math.max(1, ownedCounts[wantedFeature.id] || 0) });
  }, [ownedCounts, wantedFeature, plans]);

  // Everything the app holds, plus everything it could buy. An extra the app
  // already has stays listed on any plan — keeping is never gated.
  const rows = useMemo(() => {
    const byId = new Map<string, { id: string; name: string; price: number; kind?: string; maxStorageBytes?: number | null; planIds: string[] }>();

    for (const feature of catalog || []) {
      byId.set(feature.id, feature);
    }

    for (const row of owned || []) {
      if (!byId.has(row.featureId)) {
        byId.set(row.featureId, {
          id: row.featureId,
          name: row.name,
          price: row.price,
          // Not in the catalog any more (disabled, or repriced away). An
          // engine means it is a database, which is what the floor rule needs.
          kind: row.engine ? 'database' : undefined,
          maxStorageBytes: row.maxStorageBytes,
          planIds: [],
        });
      }
    }

    return Array.from(byId.values());
  }, [catalog, owned]);

  const extras = rows
    .map((row) => ({ featureId: row.id, quantity: counts[row.id] || 0 }))
    .filter((extra) => extra.quantity > 0);
  const extrasTotal = rows.reduce((sum, row) => sum + row.price * (counts[row.id] || 0), 0);
  const ownedTotal = rows.reduce((sum, row) => sum + row.price * (ownedCounts[row.id] || 0), 0);
  const total = (draftPlan?.price ?? currentPlan?.price ?? 0) + extrasTotal;
  const currentTotal = (currentPlan?.price || 0) + ownedTotal;

  const isPlanChange = Boolean(draftPlan && draftPlan.id !== currentPlan?.id);
  const changedRows = rows.filter((row) => (counts[row.id] || 0) !== (ownedCounts[row.id] || 0));
  // A Free app buys by picking a plan. A paid one — replacing its legacy
  // subscription included — needs something to actually change.
  const hasChange = mode === 'CHECKOUT' && !replacesPreviousPlan
    ? Boolean(draftPlan)
    : isPlanChange || changedRows.length > 0;

  // A scheduled cancellation is deliberately NOT a block: paying for a plan
  // lifts it as part of the same change — the server does both or neither.
  const blockedReason = !isOwner
    ? 'Only the app owner can change the plan.'
    : isPendingPayment
    ? 'Pay for this app on the Overview before changing its plan.'
    : paymentPastDueAt
    ? 'The last payment failed. Update the card on the Overview before changing the plan.'
    : undefined;
  const canEdit = !blockedReason && !isLegacyBilling && step === 'select';

  const discard = () => {
    setCounts(ownedCounts);
    setDraftPlanId(isPaid ? currentPlan?.id : undefined);
    setErrorMessage('');
  };

  // Free is a row only while the app is on it. Leaving a paid plan is a
  // cancellation, with its own confirmation, not a plan like the others.
  const pick = (plan: Plan) => {
    if (plan.price > 0) {
      setDraftPlanId(plan.id);
    } else {
      // Back to Free on a Free app: nothing picked, nothing in the basket.
      discard();
    }
  };

  const setCount = (featureId: string, next: number) =>
    setCounts((current) => ({ ...current, [featureId]: Math.max(0, next) }));

  /**
   * Add a pack, choosing a plan first if nothing is chosen yet: wanting the
   * extra IS a plan decision, so the cheapest paid plan that sells it is
   * picked along with it — the first row of its group, marked, one click from
   * being changed.
   */
  const addPack = (row: { id: string; planIds: string[] }, next: number) => {
    if (!draftPlan) {
      const entry = (plans || [])
        .filter((plan) => plan.price > 0 && row.planIds.includes(plan.id))
        .sort((a, b) => a.price - b.price)[0];

      if (!entry) {
        return;
      }

      setDraftPlanId(entry.id);
    }

    setCount(row.id, next);
  };

  /* ---- the actions ---- */

  const purchaseLines = () =>
    extras.map((extra) => {
      const row = rows.find((candidate) => candidate.id === extra.featureId);

      return {
        id: extra.featureId,
        name: row?.name || 'Extra',
        detail: extra.quantity > 1 ? `× ${extra.quantity}` : undefined,
        price: (row?.price || 0) * extra.quantity,
      };
    });

  const onContinue = async () => {
    if (!draftPlan || !appId) {
      return;
    }

    setErrorMessage('');

    if (mode === 'CHECKOUT') {
      if (!user) {
        return;
      }

      setIsWorking(true);

      const outcome = await purchase({
        appId,
        planId: draftPlan.id,
        extras,
        user,
        // The plan's own name here, not the one this screen lists it under.
        // The table can call the entry tier "Shared" because the columns
        // beside it say which Shared; the checkout has no columns, and its
        // heading read "Shared" over a price that is Starter's — the name on
        // the pricing page, on the receipt, and on every other checkout. The
        // spec line under it says the size that was picked.
        summary: {
          eyebrow: replacesPreviousPlan ? 'new subscription' : `upgrade to ${draftPlan.name}`,
          note: replacesPreviousPlan ? 'Your previous plan will be canceled.' : undefined,
          planName: draftPlan.name,
          planSpec: planCheckoutSpec(draftPlan),
          planPrice: draftPlan.price,
          lines: purchaseLines(),
        },
      });

      setIsWorking(false);

      if (outcome.ok) {
        // A checkout is always money taken now.
        setPaid({ charged: true, amount: money(total), plan: draftPlan });
        setStep('done');
      } else if (outcome.reason === 'FAILED') {
        setErrorMessage(outcome.message || "We couldn't complete the payment.");
      }

      return;
    }

    setIsWorking(true);

    try {
      setPreview(await back4app2.containerBillingChangePreview(appId, draftPlan.id, extras));
      setStep('pay');
    } catch (e) {
      console.error('could not preview the change', e);
      setErrorMessage(errorText(e, "We couldn't work out the price of this change. Try again in a minute."));
    } finally {
      setIsWorking(false);
    }
  };

  const onConfirmPay = async () => {
    if (!draftPlan || !appId) {
      return;
    }

    setIsWorking(true);
    setErrorMessage('');

    try {
      await back4app2.updateContainerAppBilling(appId, draftPlan.id, extras);
      setPaid({ charged: chargesNow, amount: dueNow, plan: draftPlan });
      setStep('done');
    } catch (e) {
      console.error('could not change the plan', e);
      setStep('select');
      setErrorMessage(errorText(e, "We couldn't change this plan. Nothing was charged."));
    } finally {
      setIsWorking(false);
    }
  };

  const railRef = useRef<HTMLDivElement>(null);

  const openCancel = () => {
    setErrorMessage('');
    setStep('cancel');
    railRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  };

  const onConfirmCancel = async () => {
    if (!appId) {
      return;
    }

    setIsWorking(true);
    setErrorMessage('');

    try {
      await back4app2.cancelContainerAppPlan(appId);
      onPlanCanceled?.();
    } catch (e) {
      console.error('could not cancel the plan', e);
      setErrorMessage(errorText(e, "We couldn't cancel this plan. Please try again."));
    } finally {
      setIsWorking(false);
    }
  };

  /** Call off a cancellation, or a downgrade that has not started. */
  const onKeep = async () => {
    if (!appId) {
      return;
    }

    setIsWorking(true);
    setErrorMessage('');

    try {
      if (isScheduledToCancel) {
        await back4app2.resumeContainerAppPlan(appId);
      } else {
        await back4app2.cancelScheduledPlanChange(appId);
      }
      onPlanCanceled?.();
    } catch (e) {
      console.error('could not keep the plan', e);
      setErrorMessage(errorText(e, "We couldn't keep this plan. Please try again."));
    } finally {
      setIsWorking(false);
    }
  };

  /* ---- what the rail says ---- */

  const dueNow = preview
    ? (preview.currencyCode === 'USD' ? '$' : `${preview.currencyCode} `) + (Number(preview.dueNow) / 100).toFixed(2)
    : '';
  // Going DOWN a plan: the period is already paid for, so the proration is a
  // credit rather than a charge and nothing is due. Saying "$0.00 is charged
  // now" on a button reading "Pay $0.00" reads like a broken price.
  const chargesNow = Boolean(preview) && Number(preview?.dueNow) > 0;
  // A plan that starts later — a downgrade. The app keeps the size it paid for
  // until the period ends, so the rail says a date, not just a price.
  const planStartsOn = preview?.planStartsAt ? new Date(preview.planStartsAt) : undefined;

  /**
   * "Shared 1 GB": the size stays on the name here, unlike in the list. A
   * heading has no columns beside it, and three of the plans on offer are
   * called "Shared" — without it the first line of a change would read
   * "Shared → Shared".
   */
  const planTitle = (plan?: Plan) =>
    !plan || plan.price === 0 ? planDisplayName(plan) || 'Free' : `${planDisplayName(plan)} ${formatRAM(plan.maxRAM)}`;
  const currentName = planTitle(currentPlan);
  const nextPlan = draftPlan || currentPlan;
  const nextName = planTitle(nextPlan);
  const activeExtras = (owned || []).filter((feature) => feature.status === 'ACTIVE');
  const pendingExtras = (owned || []).filter((feature) => feature.status !== 'ACTIVE');
  const ownedSpec = [
    ...activeExtras.map((feature) => `${feature.quantity} × ${feature.name}`),
    ...pendingExtras.map((feature) => `${feature.quantity} × ${feature.name} (pending payment)`),
  ].join(' · ');
  const draftSpec = [
    planSpec(nextPlan),
    ...rows.filter((row) => (counts[row.id] || 0) > 0).map((row) => `${counts[row.id]} × ${row.name}`),
  ].filter(Boolean).join(' · ');
  // The date the plan runs until: Paddle's cycle end, or the server's own
  // dates when a cancellation or a change is already scheduled.
  const periodEnd = cancelsOn && isScheduledToCancel ? cancelsOn : changesOn && isScheduledToChange ? changesOn : cycleEnd;

  const lines = [
    { label: nextName, spec: planSpec(nextPlan), price: nextPlan?.price || 0 },
    ...rows
      .filter((row) => (counts[row.id] || 0) > 0)
      .map((row) => ({
        label: row.name,
        spec: `${counts[row.id]} × ${
          typeof row.maxStorageBytes === 'number' ? `${Math.round(row.maxStorageBytes / 1024 ** 3)} GB ` : ''
        }pack`,
        price: row.price * (counts[row.id] || 0),
      })),
  ];

  const rail = (() => {
    if (step === 'done') {
      const landed = paid?.plan ? planTitle(paid.plan) : nextName;

      return (
        <>
          <RailHead eyebrow={paid?.charged ? 'payment received' : 'plan updated'} eyebrowClassName={paid?.charged ? '!text-state-ok-soft' : ''}>
            <span className="font-sora text-ds-20 font-semibold tracking-[-0.02em] leading-[1.2] text-white">
              {planStartsOn ? `${landed} starts ${shortDate(planStartsOn)}` : `Now on ${landed}`}
            </span>
            <span className="font-courrier text-ds-115 text-navy-300">
              {[planSpec(paid?.plan), paid?.charged && paid.amount ? `${paid.amount} charged to your card` : 'nothing charged today']
                .filter(Boolean).join(' · ')}
            </span>
          </RailHead>
          <div className={`${SECTION} flex-1 min-w-0 justify-center`}>
          {planStartsOn ? (
            <p className="m-0 text-ds-125 leading-[1.5] text-navy-200">
              Until then the app keeps running {currentName}, which is already paid for.
            </p>
          ) : (
            <div className="flex items-center gap-2.5 rounded-card bg-navy-700 px-3 py-3">
              <StatusDot tone="info" halo />
              <span className="flex-1 text-ds-135 text-navy-100">Redeploying with the new plan</span>
              <button
                type="button"
                onClick={() => navigate(`/apps/${appId}/deployments`)}
                className="b4-focus rounded-[3px] text-ds-135 font-medium text-action-soft hover:text-white transition-colors duration-[120ms]"
              >
                View
              </button>
            </div>
          )}
          </div>
          {/* Clicking away leaves the same way the button does: there is
              nothing left to do here, and stopping on a stale plan list is how
              someone pays twice. */}
          <div className={`${SECTION} flex-none justify-center lg:w-[320px]`}>
            <DsButton variant="outline" size="lg" className="w-full" onClick={() => navigate(`/apps/${appId}`)}>
              Done
            </DsButton>
          </div>
        </>
      );
    }

    if (step === 'pay') {
      return (
        <>
          <RailHead eyebrow="payment">
            <div className="flex items-baseline gap-2">
              <span className="font-sora text-[15px] font-semibold tracking-[-0.01em] text-white">{nextName}</span>
              <span className="font-courrier text-ds-115 text-navy-300">{draftSpec}</span>
            </div>
          </RailHead>
          <div className={`${SECTION} flex-1 min-w-0 justify-center gap-3`}>
            {lines.map((line) => <RailLine key={line.label} {...line} />)}
          </div>
          <div className={`${SECTION} flex-none gap-4 lg:ml-auto lg:w-[320px]`}>
            <div className="flex flex-col gap-2">
              <RailTotal eyebrow="due today" amount={chargesNow ? dueNow : '$0'} unit={null} />
              <span className="text-ds-115 leading-[1.5] text-navy-200">
                {chargesNow
                  ? `Prorated for the rest of the period, charged to the card on this app's subscription. Then ${money(preview?.monthlyTotal || total)}/mo.`
                  : `Nothing is charged now — this period is already paid for. Then ${money(preview?.monthlyTotal || total)}/mo.`}
              </span>
              {isScheduledToCancel ? (
                <span className="text-ds-115 leading-[1.5] text-white">
                  The scheduled cancellation is called off — the app stays on a paid plan.
                </span>
              ) : null}
              {planStartsOn ? (
                <span className="text-ds-115 leading-[1.5] text-white">
                  {nextName} starts on {longDate(planStartsOn)}. Until then the app keeps running {currentName}, which is already paid for.
                </span>
              ) : preview?.redeploys ? (
                <span className="text-ds-115 leading-[1.5] text-navy-300">The app redeploys to run on the new plan's CPU and memory.</span>
              ) : null}
            </div>
            <div className="grid grid-cols-[auto_minmax(0,1fr)] gap-2">
              <DsButton variant="outline" size="lg" isDisabled={isWorking} onClick={() => setStep('select')}>Back</DsButton>
              <DsButton variant="primary" size="lg" isDisabled={isWorking} onClick={onConfirmPay}>
                {isWorking ? (chargesNow ? 'Charging…' : 'Applying…') : chargesNow ? `Pay ${dueNow}` : 'Confirm the change'}
              </DsButton>
            </div>
          </div>
        </>
      );
    }

    if (step === 'cancel') {
      const until = periodEnd ? `until ${longDate(periodEnd)}` : 'until the end of the period you paid for';
      const effects = [
        {
          label: periodEnd ? `Moves to Free on ${shortDate(periodEnd)}` : 'Moves to Free after the period',
          spec: [planSpec(freePlan), 'sleeps after a while without requests'].filter(Boolean).join(' · '),
        },
        ...(activeExtras.some((feature) => feature.engine)
          ? [{ label: 'Database stops being billed', spec: 'data kept for 7 days, then deleted — export a dump first' }]
          : activeExtras.length
          ? [{ label: `Extras stop being billed`, spec: activeExtras.map((feature) => `${feature.quantity} × ${feature.name}`).join(' · ') }]
          : []),
        { label: 'Domains, code and settings are kept', spec: 'pick a paid plan again any time' },
      ];

      return (
        <>
          <RailHead eyebrow="cancel plan" eyebrowClassName="!text-state-err">
            <span className="font-sora text-ds-20 font-semibold tracking-[-0.02em] leading-[1.2] text-white">Cancel {currentName}?</span>
            <span className="text-ds-135 leading-[1.5] text-navy-200">The app keeps running on {currentName} {until}, then moves to Free.</span>
          </RailHead>
          <div className={`${SECTION} flex-1 min-w-0 justify-center gap-3`}>
            {effects.map((effect) => <RailLine key={effect.label} {...effect} />)}
          </div>
          <div className={`${SECTION} flex-none gap-3 lg:ml-auto lg:w-[320px]`}>
            <span className="text-ds-115 leading-[1.5] text-navy-200">
              No refund for the current period. You can keep the plan any time{periodEnd ? ` before ${shortDate(periodEnd)}` : ''}.
            </span>
            {errorMessage ? <span className="text-ds-115 leading-[1.5] text-state-err">{errorMessage}</span> : null}
            <div className="grid grid-cols-2 gap-2">
              <DsButton variant="outline" size="lg" isDisabled={isWorking} onClick={() => { setErrorMessage(''); setStep('select'); }}>Keep plan</DsButton>
              <DsButton variant="danger" size="lg" isDisabled={isWorking} onClick={onConfirmCancel}>
                {isWorking ? 'Cancelling…' : 'Cancel plan'}
              </DsButton>
            </div>
          </div>
        </>
      );
    }

    // At rest, and while a change is being put together: the same box. Left,
    // what the app runs on and what it would run on; right, the monthly
    // total, what it means, and the one action.
    const diff = total - currentTotal;
    const cta = !hasChange ? 'Select a plan' : diff > 0 ? 'Upgrade' : 'Change plan';
    const subtext = blockedReason
      ? blockedReason
      : !hasChange
      ? !isPaid
        ? 'Pick a plan below to see the total.'
        : isScheduledToCancel && cancelsOn
        ? `Moves to Free on ${longDate(cancelsOn)} · no further charges`
        : isScheduledToChange && changesOn
        ? `Moves to ${planDisplayName(pendingPlan)} on ${longDate(changesOn)}`
        : isLegacyBilling
        ? 'Checking how this app is billed…'
        : cycleEnd
        ? `Renews ${longDate(cycleEnd)}`
        : 'Billed monthly'
      : mode === 'CHECKOUT'
      ? replacesPreviousPlan
        ? 'New subscription at the full price · the previous plan is canceled'
        : 'Paid at checkout · the app redeploys after payment'
      : diff > 0
      ? `+${money(diff)}/mo · pro rata today`
      : diff < 0
      ? `−${money(-diff)}/mo · from next cycle`
      : 'Same price · the app redeploys';

    return (
      <>
        <div className={`${SECTION} flex-1 min-w-0 justify-center lg:flex-row lg:items-center lg:justify-start lg:gap-6`}>
          <div className="min-w-0 flex flex-col gap-1">
            <Eyebrow>current</Eyebrow>
            <span className="flex items-center gap-2 font-sora text-[15px] font-semibold tracking-[-0.01em] text-white">
              {planDisplayName(currentPlan) || 'Free'}
              {isScheduledToCancel && cancelsOn ? <StatusPill tone="warn">ends {shortDate(cancelsOn)}</StatusPill> : null}
            </span>
            <span className="font-courrier text-ds-115 text-navy-300">{[planSpec(currentPlan), ownedSpec].filter(Boolean).join(' · ') || '—'}</span>
          </div>
          <ChevronRightIcon size={16} className="hidden lg:block flex-none text-navy-300" />
          <div className="min-w-0 flex flex-col gap-1">
            <Eyebrow>new</Eyebrow>
            <span className={`font-sora text-[15px] font-semibold tracking-[-0.01em] ${hasChange ? 'text-white' : 'text-navy-300'}`}>
              {hasChange ? planDisplayName(nextPlan) : 'Pick a plan below'}
            </span>
            <span className="font-courrier text-ds-115 text-navy-300">{hasChange ? draftSpec : '—'}</span>
          </div>
        </div>
        <div className={`${SECTION} flex-1 min-w-0 justify-center lg:flex-row lg:items-center lg:justify-start lg:gap-6`}>
          <div className="min-w-0 flex-1 flex flex-col gap-1">
            <span className="flex items-baseline gap-1.5">
              <span className="font-sora text-[28px] font-semibold tracking-[-0.02em] leading-none text-white">{money(total)}</span>
              <span className="font-courrier text-ds-115 text-navy-300">/mo</span>
            </span>
            <span className={`text-ds-115 leading-[1.5] ${errorMessage ? 'text-state-err' : blockedReason ? 'text-state-warn' : 'text-navy-200'}`}>
              {errorMessage || subtext}
              {hasChange && isScheduledToCancel && !errorMessage ? ' · calls off the scheduled cancellation' : ''}
            </span>
          </div>
          {hasChange ? (
            <div className="flex-none flex items-center gap-2">
              <DsButton variant="outline" size="lg" isDisabled={isWorking} onClick={discard}>Discard</DsButton>
              <DsButton variant="primary" size="lg" isDisabled={isWorking || Boolean(blockedReason) || isLegacyBilling} onClick={onContinue}>
                {isWorking ? 'Working…' : cta}
              </DsButton>
            </div>
          ) : !isOwner || !isPaid ? null : isScheduledToCancel || isScheduledToChange ? (
            // The way back, next to the news. Nothing is charged: the period
            // is already paid for.
            <DsButton variant="outline" size="lg" className="flex-none" isDisabled={isWorking} onClick={onKeep}>
              {isWorking ? 'Keeping…' : `Keep ${planDisplayName(currentPlan)}`}
            </DsButton>
          ) : null}
        </div>
      </>
    );
  })();

  /* ---- the list ---- */

  // The catalogue sells paid plans only; the Free row is the app's own plan,
  // so a Free app sees where it stands.
  const listed = currentPlan && !isPaid && !(plans || []).some((plan) => plan.id === currentPlan.id)
    ? [currentPlan, ...(plans || [])]
    : plans || [];
  const groups = [
    { key: 'free', label: 'free' },
    { key: 'shared', label: 'shared cpu' },
    { key: 'dedicated', label: 'dedicated cpu' },
  ]
    .map((group) => ({ ...group, plans: listed.filter((plan) => cpuKind(plan) === group.key) }))
    .filter((group) => group.plans.length && (group.key !== 'free' || !isPaid));
  const selectedId = draftPlanId || currentPlan?.id;

  return (
    <div className="flex flex-col gap-6">
      {checkoutModal}

      {isLoadingPlans ? (
        <div className="flex justify-center items-center py-10"><LoadingSpinner color="text-white" /></div>
      ) : loadingPlansErrorMessage ? (
        <ErrorMessage message={loadingPlansErrorMessage} />
      ) : (
        <div className="flex flex-col gap-6">
          {/* The box about money, on top: what the app runs on, or what the
              change below it costs and the one button that makes it real. */}
          <div ref={railRef} className="flex flex-col lg:flex-row lg:items-stretch rounded-container border border-navy-500 bg-navy-800 scroll-mt-6">
            {rail}
          </div>

          {/* Off while the rail is in the middle of something: a row picked
              under a payment would change what is being paid for. */}
          <div
            className={`min-w-0 flex flex-col gap-6 transition-opacity duration-[120ms] ${canEdit ? '' : 'opacity-50 pointer-events-none'}`}
            aria-disabled={!canEdit}
          >
            {/* The table as it was: the fonts, sizes and colours of the old
                plan picker, with the price and the button in their own
                columns. Only the rail beside it is new. */}
            <div className="rounded-container border border-navy-500 bg-navy-800 overflow-x-auto b4-thin">
              <table className="w-full min-w-[560px] border-collapse">
                <thead>
                  <tr className="border-b border-navy-700">
                    <th className="pl-[22px] pr-3 py-[13px] text-left b4-eyebrow-tight !text-ds-115 font-normal w-[30%]">Plan</th>
                    <th className="px-3 py-[13px] text-left b4-eyebrow-tight !text-ds-115 font-normal">CPU</th>
                    <th className="px-3 py-[13px] text-left b4-eyebrow-tight !text-ds-115 font-normal">RAM</th>
                    <th className="px-3 py-[13px] text-left b4-eyebrow-tight !text-ds-115 font-normal">Transfer</th>
                    <th className="px-3 py-[13px] text-left b4-eyebrow-tight !text-ds-115 font-normal">Price</th>
                    <th className="pr-[22px] pl-3 py-[13px] w-[140px]"></th>
                  </tr>
                </thead>
                {groups.map((group, groupIndex) => (
                  <tbody key={group.key} className={groupIndex > 0 ? 'border-t border-navy-700' : undefined}>
                    <tr>
                      <td colSpan={6} className={`px-[22px] ${groupIndex > 0 ? 'pt-[22px]' : 'pt-3.5'} pb-2`}>
                        <span className="b4-eyebrow !text-ds-105">{group.label}</span>
                      </td>
                    </tr>
                    {group.plans.map((plan) => {
                      const isCurrent = plan.id === currentPlan?.id;
                      const isSelected = plan.id === selectedId;

                      return (
                        <tr
                          key={plan.id}
                          onClick={isSelected ? undefined : () => pick(plan)}
                          className={[
                            'transition-colors duration-[120ms]',
                            isSelected ? 'bg-state-ok/[0.09]' : 'cursor-pointer hover:bg-navy-700/50',
                          ].join(' ')}
                        >
                          <td className="pl-[22px] pr-3 py-[15px] text-[length:calc(15px*var(--b4-type,1))] font-medium text-white whitespace-nowrap">{planDisplayName(plan)}</td>
                          <td className="px-3 py-[15px] font-courrier text-[length:calc(13px*var(--b4-type,1))] text-navy-100">{plan.maxCPU}</td>
                          <td className="px-3 py-[15px] font-courrier text-[length:calc(13px*var(--b4-type,1))] text-navy-100 whitespace-nowrap">{formatRAM(plan.maxRAM)}</td>
                          <td className="px-3 py-[15px] font-courrier text-[length:calc(13px*var(--b4-type,1))] text-navy-100 whitespace-nowrap">{plan.transferLimit ? formatTransfer(plan.transferLimit) : '—'}</td>
                          <td className="px-3 py-[15px] whitespace-nowrap">
                            <span className="text-[16px] font-semibold text-white">${plan.price}</span>
                            <span className="text-ds-125 text-navy-200"> / mo</span>
                          </td>
                          <td className="pr-[22px] pl-3 py-3 text-right">
                            <DsButton
                              variant={isSelected ? 'primary' : 'subtle'}
                              size="lg"
                              className={`min-w-[104px] ${isSelected ? 'pointer-events-none' : ''}`}
                              onClick={(event) => {
                                event.stopPropagation();
                                pick(plan);
                              }}
                            >
                              {isCurrent && isSelected ? 'Current' : isSelected ? 'Selected' : 'Select'}
                            </DsButton>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                ))}
              </table>
            </div>

            {rows.length > 0 ? (
              <div className="flex flex-col gap-3">
                <Eyebrow>extras</Eyebrow>
                {rows.map((row) => {
                  const count = counts[row.id] || 0;
                  const ownedCount = ownedCounts[row.id] || 0;
                  const isSoldOnPlan = Boolean(draftPlan && row.planIds.includes(draftPlan.id));
                  // Adding past what is owned needs the extra on the chosen
                  // plan. Without a plan chosen, adding is still offered — the
                  // first plan that sells the extra is picked along with it.
                  const canAdd = draftPlan ? isSoldOnPlan || count < ownedCount : row.planIds.length > 0;
                  // The last database pack stays until the purge exists.
                  const floor = row.kind === 'database' && ownedCount > 0 ? 1 : 0;
                  const canRemove = count > floor;

                  return (
                    <div
                      key={row.id}
                      className={
                        'rounded-container border px-5 py-[18px] flex flex-wrap items-center justify-between gap-x-6 gap-y-3 transition-colors duration-[120ms] ' +
                        // An extra with packs in it: its edge says so, the way
                        // the selected row in the table is marked.
                        (count > 0 ? 'border-state-ok bg-state-ok/5' : 'border-navy-500 bg-navy-800')
                      }
                    >
                      <div className="min-w-0 flex flex-col gap-1">
                        <span className="text-[15.5px] font-medium text-white">{row.name}</span>
                        <span className="font-courrier text-[length:calc(12px*var(--b4-type,1))] text-navy-300">
                          {[
                            `${money(row.price)} / pack`,
                            typeof row.maxStorageBytes === 'number' ? `${Math.round(row.maxStorageBytes / 1024 ** 3)} GB each` : undefined,
                            draftPlan && !isSoldOnPlan && count <= ownedCount
                              ? `not sold on ${planDisplayName(draftPlan)}`
                              : !draftPlan && row.planIds.length > 0
                              ? 'available on paid plans'
                              : undefined,
                          ].filter(Boolean).join(' · ')}
                        </span>
                      </div>

                      <div className="flex-none flex items-center gap-6">
                        <div className="inline-flex items-center h-[40px] rounded-surface border border-navy-500 overflow-hidden">
                          <button
                            type="button"
                            aria-label={`Remove a ${row.name} pack`}
                            disabled={!canRemove}
                            onClick={() => setCount(row.id, count - 1)}
                            className="b4-focus h-full w-[38px] inline-flex items-center justify-center text-[18px] text-navy-100 hover:bg-navy-700 hover:text-white disabled:text-navy-500 disabled:hover:bg-transparent disabled:cursor-not-allowed"
                          >
                            −
                          </button>
                          <span className="min-w-[44px] text-center font-courrier text-[length:calc(14px*var(--b4-type,1))] text-white" aria-live="polite">
                            {count}
                          </span>
                          <button
                            type="button"
                            aria-label={`Add a ${row.name} pack`}
                            disabled={!canAdd}
                            onClick={() => addPack(row, count + 1)}
                            className="b4-focus h-full w-[38px] inline-flex items-center justify-center text-[18px] text-navy-100 hover:bg-navy-700 hover:text-white disabled:text-navy-500 disabled:hover:bg-transparent disabled:cursor-not-allowed"
                          >
                            +
                          </button>
                        </div>
                        <span className="min-w-[96px] text-right whitespace-nowrap">
                          <span className="font-semibold text-[16px] text-white">{money(row.price * count)}</span>
                          <span className="text-ds-125 text-navy-300"> / mo</span>
                        </span>
                      </div>
                    </div>
                  );
                })}
                {/* How much of the packs is in use — nothing for an app that
                    bought none. */}
                {usage && usage.packs > 0 ? (
                  <div className="rounded-container border border-navy-500 bg-navy-800 overflow-hidden [&>div]:border-t-0">
                    <StorageUsageStrip usage={usage} />
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
      )}

      {/* At the foot of the page, and quiet: leaving is one click, but it is
          not what this screen is for. Only an owner, only a paid plan — there
          is nothing to cancel on Free — and never while a cancellation is
          already scheduled: the way back from that lives in the box. */}
      {isOwner && isPaid && !isScheduledToCancel && !isLoadingPlans && !loadingPlansErrorMessage ? (
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 pt-5 mt-2 border-t border-navy-700 text-ds-125 text-navy-300">
          <span>You keep the plan until the period you paid for ends.</span>
          <button
            type="button"
            disabled={Boolean(blockedReason) || step !== 'select'}
            onClick={openCancel}
            className="b4-focus rounded-control underline underline-offset-[3px] hover:text-state-err disabled:hover:text-navy-300 disabled:cursor-not-allowed transition-colors duration-[120ms]"
          >
            Cancel plan
          </button>
        </div>
      ) : null}
    </div>
  );
};

export default PlanCheckout;
